import { NgClass, NgTemplateOutlet, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ImageModule } from '@df/module-image/image.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NTextOverlay, TextOverlayComponent } from 'projects/app-default/src/app/common/text-overlay/text-overlay.component';
import { ShopThisLookComponent } from 'projects/app-default/src/app/ui/shop-this-look/shop-this-look.component';
import { NShopThisLook } from 'projects/app-default/src/app/ui/shop-this-look/shop-this-look.interface';
import { SwiperModule } from 'swiper/angular';
import { getCmsContentFieldConfig } from '../../../../../cms/components/components.config';
import { ICmsComponentContentData, ICmsContentVariant, ICmsContentVideoProvider } from '../../../../../cms/components/components.interface';
enum EImageVariants {
    Two_images = 'two_images',
    Three_images = 'three_images',
    Five_images = 'five_images'
}
export interface IPageComponentTrendingImageBlock extends NTextOverlay.IData, NShopThisLook.IData {
    image?: string;
    description?: string;
    link?: string;
}
export interface IPageComponentTrendingData extends NICmsContentComponent.IData, ICmsComponentContentData {
    variant: EImageVariants;
    reverse: boolean;
    description_below: boolean;
    title?: string;
    subtitle?: string;
    cta_subtitle_copy?: string;
    cta_subtitle_link?: string;
    cta_subtitle_color?: string;
    show_cta_subtitle: boolean;
    image_blocks?: IPageComponentTrendingImageBlock[];
}
@ICmsComponent<PageComponentTrendingComponent>({
    id: 'trending',
    tags: ['category', 'trending'],
    name: 'Trending',
    fields: [
        // HEADER
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            description: 'Title copy',
            default: 'Saddlery & Tack'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'subtitle',
            title: 'Subtitle',
            description: 'Subtitle copy',
            default: 'Essentials for your Tack Room'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cta_subtitle_copy',
            title: 'CTA subtitle',
            description: 'CTA Subtitle copy',
            default: 'View All'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'cta_subtitle_link',
            title: 'CTA subtitle link',
            description: 'CTA Subtitle link',
            default: '/'
        },
        {
            type: NICmsContentField.EFieldType.colorPicker,
            key: 'cta_subtitle_color',
            title: 'CTA subtitle colour',
            description: 'CTA subtitle colour',
            default: '#000000',
            canShow: (data: IPageComponentTrendingData) => data.variant === EImageVariants.Three_images
        },
        {
            type: NICmsContentField.EFieldType.toggle,
            key: 'show_cta_subtitle',
            title: 'Should CTA subtitle be displayed?',
            description: 'Choose if CTA subtitle is displayed or not',
            default: true
        },
        // COMPONENT VARIANT AND REVERSING IMAGE ORDER
        <NICmsContentField.IChoiceField>{
            key: 'variant',
            type: NICmsContentField.EFieldType.choice,
            title: 'Component variant',
            default: EImageVariants.Three_images,
            options: [
                { key: EImageVariants.Two_images, title: '2 big images' },
                { key: EImageVariants.Three_images, title: '3 big images' },
                { key: EImageVariants.Five_images, title: '1 big and 4 small images' }
            ]
        },
        {
            key: 'reverse',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Small images on top?',
            description: 'Choose if small images appear above the big one',
            default: false,
            canShow: (data: IPageComponentTrendingData) => data.variant === EImageVariants.Five_images
        },
        {
            key: 'description_below',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Description below image?',
            description: 'Choose if CTA / description should be displayed on the image or below',
            default: false
        },
        // IMAGES REPEATER
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'image_blocks',
            title: 'Image Blocks',
            itemName: 'Image no.',
            itemNameField: 'description',
            defaultSize: 3,
            maxSize: 5,
            fields: [
                ...getCmsContentFieldConfig('image'),
                ...NTextOverlay.getFields(true),
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'description',
                    title: 'Description of the image',
                    description: 'Hidden if overflow text exists and not description below'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'link',
                    title: 'Link for the image'
                },
                ...NShopThisLook.fields
            ]
        }
    ]
    // initialData: {
    //     styles: {
    //         margin_top: 'm-t-10'
    //     }
    // }
})
@Component({
    selector: 'page-component-trending',
    templateUrl: './trending.html',
    styleUrls: ['./trending.scss'],
    standalone: true,
    imports: [
        NgClass,
        SizeClassDirective,
        ICmsClientModule,
        RouterLink,
        SlicePipe,
        NgTemplateOutlet,
        DfUiModule,
        SwiperModule,
        ShopThisLookComponent,
        HrefComponent,
        ShellComponent,
        ImageModule,
        TextOverlayComponent,
        VimeoPlayerComponent,
        YoutubePlayerComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentTrendingComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentTrendingData;
    contentVariant = ICmsContentVariant;
    contentVideoProvider = ICmsContentVideoProvider;
    get isThreeImagesVariant() {
        return this.component.variant === EImageVariants.Three_images;
    }
}
